<template>
    <section v-if="hasLunch" class="lunch-list">
        <div class="inner">
            <button
                class="ds-btn ds-btn--primary btn-open"
                :class="{'is-open': open}"
                type="button"
                @click="open = !open"
            >
                <span>{{ $i18n.t('open') }}</span>
                <DSIcon
                    id="weekly-lunch-list-toggle"
                    class="toggle-icon"
                    size="l"
                    code="chevron-down"
                    aria-hidden="true"
                    :aria-expanded="open"
                    aria-controls="weekly-lunch-list"
                />
            </button>
            <DSHeightTransition :open>
                <div
                    id="weekly-lunch-list"
                    class="list"
                    aria-labelledby="weekly-lunch-list-toggle"
                    :aria-hidden="!open"
                >
                    <div
                        v-for="(item, key) in lunchList"
                        :key="`item-${key}`"
                        class="lunch-list-item"
                        :class="{
                            ['today']: item.activeLunch,
                        }"
                    >
                        <h3 class="h4">{{ `${$t(`weekday.${item.date.toFormat('cccc')}`)} ${item.date.toFormat('d.M.')}` }}</h3>
                        <div
                            v-for="(menuItem, mKey) in item.items.products"
                            :key="`menu-item-${mKey}`"
                            class="item"
                        >
                            <span>
                                {{ menuItem.name }}<!--
                                --><br v-if="!$bp.bps.isSmallDesktop">
                                <span v-else-if="menuItem.specialties">, </span>
                                {{ menuItem.specialties ? menuItem.specialties.join(', ') : '' }}
                            </span>
                            <strong>{{ $cms.formatPrice(menuItem.price) }}</strong>
                        </div>
                        <DSCFText
                            v-if="item.items.placeholderText"
                            :content="item.items.placeholderText"
                        />
                    </div>
                </div>
            </DSHeightTransition>
        </div>
    </section>
</template>
<script setup>
import { ref, computed, useI18n, useNuxtApp } from '#imports';
import { DateTime } from 'luxon';
import { usePageStore } from '~dsc/stores/cms/page';

const { $cms, $bp, $i18n } = useNuxtApp();
const pageStore = usePageStore();
const open = ref(false);

defineProps({
    stations: {
        type: Object,
        required: false,
        default: () => ({}),
    },
});

const lunchList = computed(() => {
    const date = DateTime.now().startOf('week');
    const lunch = pageStore?.station?.lunch ?? {};
    const lunchTimeStamp = lunch?.currentLunchTimeStamp ?? DateTime.now().toMillis();
    const currentLunchWeekday = DateTime.fromMillis(lunchTimeStamp).weekday - 1;
    const { days = [] } = lunch?.week ?? { days: [] };

    return [0, 1, 2, 3, 4, 5, 6].map(weekday => ({
        date: date.plus({ days: weekday }),
        items: days[weekday] ?? [],
        activeLunch: weekday === currentLunchWeekday,
    }));
});

const hasLunch = computed(() => lunchList.value.some(lunchDate => lunchDate?.items?.id));

useI18n({
    useScope: 'global',
    messages: {
        'fi-FI': {
            open: 'Avaa viikon lounaslista',
        },
        'en-EU': {
            open: 'Open weekly lunch menu',
        },
        'sv-SE': {
            open: 'Öppna veckans lunchmeny',
        },
    },
});
</script>
<style scoped>
.lunch-list {
  padding-top: 0.25rem;
  display: grid;
  max-width: var(--desktop-max-width);
  width: 100%;
  margin: 0 auto;
}
.lunch-list .inner {
  max-width: var(--page-max-width);
  margin: 0 auto;
  padding-left: 1.5rem;
  width: 100%;
  padding-right: 1.5rem;
}

.inner {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.btn-open {
  margin: 1rem 0;
  font-size: 20px;
  padding: 1rem 1.5rem;
}
.btn-open.is-open .toggle-icon {
  transform: scaleY(-100%);
}

.toggle-icon {
  margin-left: 1rem;
  transition: transform 0.3s ease;
}

.list {
  display: flex;
  flex-flow: column wrap;
  max-width: 760px;
  width: 100%;
  margin-top: 0.25rem;
}
.list .lunch-list-item {
  padding: 1.5rem;
  border: 1px solid var(--color-base-grey-300);
  border-radius: 0.25rem;
}
.list .lunch-list-item:not(:last-child) {
  margin-bottom: 0.5rem;
}
.list .lunch-list-item.today {
  position: relative;
}
.list .lunch-list-item.today::before {
  content: "";
  width: 0.375rem;
  height: 100%;
  left: 0;
  top: 0;
  border-radius: 0.25rem 0 0 0.25rem;
  background-color: var(--color-primary-500);
  position: absolute;
}
.list .lunch-list-item .item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.25rem 1rem;
  min-height: 2.375rem;
}
.list .lunch-list-item .item:nth-child(2n) {
  background: var(--color-secondary-500);
}
.list .h4 {
  color: var(--color-primary-500);
}
</style>
